/* -------------------------------------------------------------------------
   Anbindung der Mietpreis-Analyse ans Backend.

   Die Rechnung und der Datensatz liegen serverseitig — hier wird nur noch
   gefragt und angezeigt. Das hat zwei Gründe: Der Datensatz soll nicht
   ausgeliefert werden, und es soll nur eine Implementierung der §-558-Logik
   geben, die auch der Report benutzt.

   Fehler werden bewusst weich behandelt: Ortsauflösung und Bezirksermittlung
   dürfen ausfallen, ohne das Formular zu blockieren — die Analyse rechnet dann
   eine Stufe gröber. Nur die Auswertung selbst meldet einen Fehler nach oben.
   ------------------------------------------------------------------------- */

import { buildDashboardApiUrl } from '../../dashboard/apiConfig';

const url = (pfad) => buildDashboardApiUrl(`/api/mietpreis${pfad}`);

/** Postleitzahl → Gemeinde(n) und deren Stadtbezirke. */
export async function orteZurPlz(plz, { signal } = {}) {
  if (!/^\d{5}$/.test(String(plz))) return { orte: [], bezirke: [] };
  try {
    const r = await fetch(`${url('/ort')}?plz=${encodeURIComponent(plz)}`, { signal });
    if (!r.ok) return { orte: [], bezirke: [] };
    return await r.json();
  } catch {
    return { orte: [], bezirke: [] };
  }
}

/** Straße + PLZ → Stadtbezirk. Die Adresse geht über unseren Server, nicht
 *  vom Browser aus an einen Dritten. */
export async function bezirkFuerStrasse(strasse, plz, ags, { signal } = {}) {
  const s = String(strasse || '').trim();
  if (s.length < 3 || !/^\d{5}$/.test(String(plz)) || !ags) return null;
  const q = new URLSearchParams({ plz, ags, strasse: s });
  try {
    const r = await fetch(`${url('/bezirk')}?${q}`, { signal });
    if (!r.ok) return null;
    const d = await r.json();
    return d.bezirk ? d : null;
  } catch {
    return null;
  }
}

/** Fristen des § 558 Abs. 1 zu einer letzten Mieterhöhung. */
export async function fristAbfragen({ art, jahr, monat }, { signal } = {}) {
  const q = new URLSearchParams({ art: art || 'unbekannt' });
  if (jahr) q.set('jahr', jahr);
  if (monat) q.set('monat', monat);
  const r = await fetch(`${url('/frist')}?${q}`, { signal });
  if (!r.ok) throw new Error(`Fristabfrage fehlgeschlagen (${r.status})`);
  return r.json();
}

/** Auswertung anfordern UND den Bericht an die angegebene Adresse schicken.
 *  Die Auswertung kommt sofort zurück; der Bericht wird serverseitig im
 *  Hintergrund gerendert und versendet. */
export async function berichtAnfordern(eingabe, { signal } = {}) {
  const r = await fetch(url('/report'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(eingabe),
    signal,
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(`Auswertung fehlgeschlagen (${r.status}) ${text.slice(0, 200)}`);
  }
  return r.json();
}

/** Vollständige Auswertung. Wirft bei technischem Fehler — hier will der
 *  Nutzer ein Ergebnis sehen, ein stilles Nichts wäre irreführend. */
export async function analysiere(eingabe, { signal } = {}) {
  const r = await fetch(url('/analyse'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(eingabe),
    signal,
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(`Analyse fehlgeschlagen (${r.status}) ${text.slice(0, 200)}`);
  }
  const d = await r.json();
  return d.ergebnis || null;
}
